"use client";
import { createClientLogger } from "@/lib/logging/client";
import { loadRazorpaySdkScript } from "@/lib/razorpay-script-loader";

const logger = createClientLogger("lib/razorpay-custom-checkout");

export type RazorpayCustomPaymentSuccess = {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature?: string;
};

export type RazorpayCustomPaymentData = {
  key: string;
  amount: number;
  currency: string;
  order_id: string;
  method: "card" | "upi";
  callback_url?: string;
  redirect?: boolean;
  /**
   * Constructor option, not payment data: see `CustomInstanceOptions`. Set it
   * for a named-app UPI payment inside the native shell, or the SDK refuses
   * the app locally with `intent_no_apps_error` before any network call.
   */
  webview_intent?: boolean;
  email?: string;
  contact?: string;
  customer_id?: string;
  token?: string;
  save?: 0 | 1;
  notes?: Record<string, string>;
  vpa?: string;
  "card[name]"?: string;
  "card[number]"?: string;
  "card[cvv]"?: string;
  "card[expiry_month]"?: string;
  "card[expiry_year]"?: string;
};

type RazorpayPaymentEventTarget = {
  on: (
    event: "payment.success" | "payment.error",
    handler: (payload: unknown) => void,
  ) => RazorpayPaymentEventTarget;
};

type RazorpayCustomInstance = RazorpayPaymentEventTarget & {
  createPayment?: (
    data: Omit<RazorpayCustomPaymentData, "key" | "currency">,
    options?: Record<string, unknown>,
  ) => RazorpayPaymentEventTarget | undefined;
  checkPaymentAdapter?: (adapter: string) => Promise<void>;
  /**
   * Razorpay's client-side VPA check. Documented as resolving for a valid
   * address and rejecting for an invalid one, on `key_id` alone — no secret and
   * no server round trip. Optional here because it only exists on the Custom
   * Checkout SDK, and only while UPI Collect is available.
   */
  verifyVpa?: (vpa: string) => Promise<unknown>;
  /**
   * The UPI apps reachable on this platform.
   *
   * Reads as detection and is not: in the SDK this resolves a static array
   * chosen from the user agent and never touches the device. Treat it as a
   * platform filter — iOS returns ~8 shortcodes, Android ~13 plus `any`,
   * desktop returns none — and learn actual availability from failures.
   */
  getSupportedUpiIntentApps?: () => Promise<unknown>;
};

type RazorpayCustomConstructor = new (options: {
  key: string;
  image?: string;
  callback_url?: string;
  redirect?: boolean;
  webview_intent?: boolean;
}) => RazorpayCustomInstance;

let razorpayJsLoadPromise: Promise<void> | null = null;
const RAZORPAY_JS_SRC = "https://checkout.razorpay.com/v1/razorpay.js";
const RAZORPAY_JS_STATUS_ATTR = "data-razorpay-js-status";
const RAZORPAY_CHECKOUT_SCRIPT_SRC =
  "https://checkout.razorpay.com/v1/checkout.js";
const RAZORPAY_SCRIPT_TIMEOUT_MS = 15000;

/**
 * How long to wait for a payment to resolve before giving up on it.
 *
 * This is a CLIENT giveup, not a payment failure — the collect request is still
 * live at NPCI when it fires, and the webhook will still settle the booking. The
 * old value was 120s, which is inside the ~5 minute window a payer actually has
 * to approve a UPI collect in their bank app: the screen declared "the payment
 * didn't complete" while the payment was still perfectly completable, and the
 * money then landed against a booking the person had been told had failed.
 *
 * Matched to the NPCI window instead, so the timeout means what it says.
 */
const RAZORPAY_PAYMENT_TIMEOUT_MS = 300000;

/**
 * The provider's own account of why a charge never happened.
 *
 * Razorpay's `payment.error` payload is structured — `code`, `reason`, `step`,
 * `source`, and a `metadata.payment_id` when one exists. All of it used to be
 * collapsed into a display string, so a UPI submit that the provider refused
 * outright ("invalid vpa", "collect not enabled") reached our telemetry as
 * nothing at all: an order created, no payment object anywhere in Razorpay,
 * and no record on our side of what was said. Keeping the codes is the
 * difference between "13 payments failed" and "13 payments failed because the
 * account cannot send collect requests".
 */
export type RazorpayProviderError = {
  /** e.g. `BAD_REQUEST_ERROR`, `GATEWAY_ERROR`. */
  code: string | null;
  /** The machine cause, e.g. `invalid_vpa`, `payment_failed`, `NA`. */
  reason: string | null;
  /** `customer`, `business`, `gateway`, `internal`, `bank`, `NA`. */
  source: string | null;
  /** Where in the flow it broke, e.g. `payment_initiation`. */
  step: string | null;
  /** Present only when the provider got as far as creating a payment. */
  paymentId: string | null;
};

const readString = (value: unknown) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

export const readProviderError = (
  payload: unknown,
): { message: string; providerError: RazorpayProviderError } => {
  const empty: RazorpayProviderError = {
    code: null,
    reason: null,
    source: null,
    step: null,
    paymentId: null,
  };

  if (payload instanceof Error && payload.message.trim()) {
    return { message: payload.message, providerError: empty };
  }

  if (typeof payload === "object" && payload !== null) {
    const record = payload as Record<string, unknown>;
    const error =
      typeof record.error === "object" && record.error !== null
        ? (record.error as Record<string, unknown>)
        : record;
    const metadata =
      typeof error.metadata === "object" && error.metadata !== null
        ? (error.metadata as Record<string, unknown>)
        : {};

    const providerError: RazorpayProviderError = {
      code: readString(error.code),
      reason: readString(error.reason),
      source: readString(error.source),
      step: readString(error.step),
      paymentId: readString(metadata.payment_id),
    };

    const message =
      readString(error.description) ??
      providerError.reason ??
      "Payment failed. Please try again.";

    return { message, providerError };
  }

  return { message: "Payment failed. Please try again.", providerError: empty };
};

export async function loadRazorpayJs(options?: {
  forceReload?: boolean;
}): Promise<
  { ok: true; data: { loaded: true } } | { ok: false; message: string }
> {
  // Standard Checkout and Custom Checkout replace the same global. Do not
  // trust an old resolved promise after the Intent/QR flow owned it.
  if (
    options?.forceReload ||
    (typeof window !== "undefined" &&
      window.__hiiveRazorpaySdk &&
      window.__hiiveRazorpaySdk !== "custom")
  ) {
    razorpayJsLoadPromise = null;
  }

  if (!razorpayJsLoadPromise) {
    razorpayJsLoadPromise = loadRazorpaySdkScript({
      src: RAZORPAY_JS_SRC,
      sdk: "custom",
      statusAttribute: RAZORPAY_JS_STATUS_ATTR,
      isReady: () => Boolean(window.Razorpay),
      forceReload: options?.forceReload ?? false,
      timeoutMs: RAZORPAY_SCRIPT_TIMEOUT_MS,
      browserMessage: "Payments can only be loaded in the browser.",
    }).then((result) => {
      if (!result.ok) {
        throw new Error(result.message);
      }
    });
  }

  try {
    await razorpayJsLoadPromise;
  } catch (error) {
    razorpayJsLoadPromise = null;
    return {
      ok: false,
      message:
        error instanceof Error
          ? error.message
          : "Failed to load payment provider.",
    };
  }

  if (!window.Razorpay) {
    return { ok: false, message: "Payment provider did not initialize." };
  }

  return { ok: true, data: { loaded: true } };
}

/**
 * Options that shape a Custom Checkout instance rather than a payment.
 *
 * `webviewIntent` is the one that needs explaining. Read from
 * `checkout.razorpay.com/v1/razorpay.js` (2026-09-25): the SDK's app list
 * (`os()`) returns `[]` inside any WebView — its `Ln`/`Wn` user-agent tests
 * match the Capacitor shells on both platforms — unless the *most recently
 * constructed* instance carries `webview_intent`. The same list gates
 * `createPayment(data, { app })`: a shortcode missing from it is refused
 * locally with `intent_no_apps_error` 100ms later. So inside the native shell
 * both the listing instance and the paying instance must be built with it, and
 * because the SDK reads whichever instance was constructed last, each call
 * builds its own immediately before asking.
 */
type CustomInstanceOptions = {
  callbackUrl?: string;
  redirect?: boolean;
  webviewIntent?: boolean;
};

const createCustomInstance = (
  key: string,
  options: CustomInstanceOptions = {},
) => {
  if (!window.Razorpay) return null;
  const RazorpayCtor = window.Razorpay as unknown as RazorpayCustomConstructor;
  return new RazorpayCtor({
    key,
    ...(options.callbackUrl ? { callback_url: options.callbackUrl } : {}),
    ...(options.redirect !== undefined ? { redirect: options.redirect } : {}),
    ...(options.webviewIntent ? { webview_intent: true } : {}),
  });
};

/**
 * Ask the provider whether a UPI ID exists, from the browser.
 *
 * The client-side twin of `validateRazorpayVpa`, and — unlike it — this one
 * actually answers. Confirmed against our live key on 2026-08-28:
 * `POST /v1/payments/validate/vpa` returns the account-gate 400 for every
 * address alike (`success@razorpay` included), while `razorpay.verifyVpa`
 * comes back with a real per-address verdict:
 *
 *     { vpa, success: false, customer_name: null, status_code: 200 }
 *
 * Two things about that payload are load-bearing. It arrives through the
 * promise's **reject** path even though nothing went wrong — `verifyVpa`
 * rejects on `success: false` — so the answer has to be parsed identically in
 * both branches. And a genuine negative is distinguished by carrying
 * `status_code: 200` with a boolean `success`, which is what separates it from
 * the entitlement 400 that means we could not check at all.
 *
 * Three states, never a verdict the caller is forced to obey:
 *
 * - `exists`      — resolved, or answered `success: true`. `customerName` is
 *                   the name registered against the address, when given.
 * - `not_found`   — answered `success: false` on a 200. Safe to refuse on.
 * - `unavailable` — anything else: the entitlement 400, an unreadable body, a
 *                   transport failure, an SDK without the method. Means "we
 *                   could not check", and must never refuse a payment.
 */
export type RazorpayVpaVerdict = {
  status: "exists" | "not_found" | "unavailable";
  /** Only ever set alongside `exists`; drives the "Verified · Name" line. */
  customerName: string | null;
  /** The provider's own words, for logging. */
  detail: string | null;
};

export const readVpaPayload = (payload: unknown) => {
  if (typeof payload !== "object" || payload === null) return null;
  const record = payload as Record<string, unknown>;
  // A definitive answer is a 200 carrying a boolean. Anything else — notably
  // the `{error:{code:"BAD_REQUEST_ERROR",source:"internal"}}` entitlement
  // refusal — is not an answer about the address.
  if (record.status_code !== 200 || typeof record.success !== "boolean") {
    return null;
  }
  return {
    exists: record.success,
    customerName: readString(record.customer_name),
  };
};

export async function verifyRazorpayVpa(
  key: string,
  vpa: string,
): Promise<RazorpayVpaVerdict> {
  const load = await loadRazorpayJs();
  if (!load.ok) {
    return { status: "unavailable", customerName: null, detail: load.message };
  }

  const instance = createCustomInstance(key);
  if (!instance || typeof instance.verifyVpa !== "function") {
    return {
      status: "unavailable",
      customerName: null,
      detail: "verifyVpa_unsupported",
    };
  }

  try {
    const resolved = readVpaPayload(await instance.verifyVpa(vpa));
    // Resolving is itself the documented "valid" signal, so an unreadable body
    // on this branch still counts as a pass.
    return {
      status: resolved && !resolved.exists ? "not_found" : "exists",
      customerName: resolved?.customerName ?? null,
      detail: null,
    };
  } catch (error) {
    const answered = readVpaPayload(error);
    if (answered) {
      return {
        status: answered.exists ? "exists" : "not_found",
        customerName: answered.customerName,
        detail: null,
      };
    }

    const { message, providerError } = readProviderError(error);
    logger.warn("[payments] razorpay_verify_vpa_unavailable", {
      code: providerError.code ?? "unknown",
      reason: providerError.reason ?? "unknown",
      source: providerError.source ?? "unknown",
      description: message,
    });
    return { status: "unavailable", customerName: null, detail: message };
  }
}

/**
 * Which UPI apps this platform can be handed a payment for.
 *
 * Resolves shortcodes (`gpay`, `phonepe`, …, plus `any` on Android) suitable for
 * `createPayment(data, { app })`. Empty on desktop, and empty whenever the SDK
 * cannot answer — callers must treat an empty list as "offer no tiles" rather
 * than falling back to a guessed set, since a guessed tile is exactly what
 * lands somebody on "Unable to open UPI app".
 *
 * Pass `webviewIntent` inside the native shell, where the SDK otherwise lists
 * nothing at all — see `CustomInstanceOptions`.
 */
export async function listSupportedUpiIntentApps(
  key: string,
  options: { webviewIntent?: boolean } = {},
): Promise<string[]> {
  const load = await loadRazorpayJs();
  if (!load.ok) return [];

  const instance = createCustomInstance(key, {
    webviewIntent: options.webviewIntent,
  });
  if (!instance || typeof instance.getSupportedUpiIntentApps !== "function") {
    logger.warn("[payments] razorpay_upi_intent_apps_unsupported", {});
    return [];
  }

  try {
    const resolved = await instance.getSupportedUpiIntentApps();
    if (!Array.isArray(resolved)) return [];
    return resolved.filter(
      (entry): entry is string => typeof entry === "string" && entry.length > 0,
    );
  } catch (error) {
    // Documented as rejecting when no apps are available, so this is an
    // ordinary "nothing to offer" and not an error worth surfacing.
    const { message } = readProviderError(error);
    logger.info("[payments] razorpay_upi_intent_apps_empty", {
      detail: message,
    });
    return [];
  }
}

/**
 * The one failure that means "ask somebody else", not "the payment failed".
 *
 * Custom and Standard Checkout publish the same `window.Razorpay`, so a page
 * that has already opened Standard can end up holding a constructor with no
 * `createPayment` on it. Callers with a Standard Checkout fallback match this
 * exact string to decide it is safe to open one — every other failure here is
 * either the provider's verdict or a payment that may still be live, and
 * opening a second checkout over either of those risks a double charge.
 */
export const CUSTOM_SDK_UNAVAILABLE_MESSAGE =
  "Custom payment SDK is unavailable. Please use provider checkout.";

export type RazorpayCustomPaymentResult =
  | { ok: true; data: RazorpayCustomPaymentSuccess }
  | {
      ok: false;
      message: string;
      /** Only set when the provider itself refused. Absent for our own
       * timeouts and SDK-loading failures, which are not its verdict. */
      providerError?: RazorpayProviderError;
    };

export async function createRazorpayCustomPayment(
  data: RazorpayCustomPaymentData,
  options?: Record<string, unknown>,
): Promise<RazorpayCustomPaymentResult> {
  const load = await loadRazorpayJs();
  if (!load.ok) return load;

  if (!window.Razorpay) {
    return { ok: false, message: "Payment provider is unavailable." };
  }

  const {
    key,
    currency: _currency,
    callback_url,
    redirect,
    webview_intent,
    ...paymentData
  } = data;
  const instanceOptions: CustomInstanceOptions = {
    callbackUrl: callback_url,
    redirect,
    webviewIntent: webview_intent,
  };
  let instance = createCustomInstance(key, instanceOptions);
  if (!instance) {
    return { ok: false, message: "Payment provider is unavailable." };
  }

  if (typeof instance.createPayment !== "function") {
    const reload = await loadRazorpayJs({ forceReload: true });
    if (!reload.ok) return reload;
    instance = createCustomInstance(key, instanceOptions);
    if (!instance) {
      return { ok: false, message: "Payment provider is unavailable." };
    }
  }

  return await new Promise<RazorpayCustomPaymentResult>((resolve) => {
    let settled = false;
    const settle = (result: RazorpayCustomPaymentResult) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      window.removeEventListener("pagehide", onPageHide);
      resolve(result);
    };

    /**
     * In redirect mode the happy path is that this promise is never settled at
     * all: Razorpay form-POSTs away and the whole JS context dies with the
     * document. Handing back a result on the way out would race the unload and
     * could flash a failure over a payment that is proceeding normally, so the
     * listener only exists to stop the timeout from firing into a dead page.
     */
    const onPageHide = () => {
      settled = true;
      window.clearTimeout(timeout);
    };
    if (redirect) {
      window.addEventListener("pagehide", onPageHide);
    }

    /**
     * One window for every mode, deliberately.
     *
     * A shorter "the redirect should have started by now" guard is tempting, but
     * redirect mode is also what UPI collect runs under, and a collect legitimately
     * sits on this page for minutes while the payer approves in their bank app.
     * A tight guard would abandon exactly the payments most likely to succeed.
     */
    const timeout = window.setTimeout(() => {
      settle({
        ok: false,
        message: "Payment did not respond. Please try again.",
      });
    }, RAZORPAY_PAYMENT_TIMEOUT_MS);

    const bind = (target: RazorpayPaymentEventTarget) => {
      target
        .on("payment.success", (response) => {
          const record = response as Partial<RazorpayCustomPaymentSuccess>;
          if (!record.razorpay_payment_id || !record.razorpay_order_id) {
            settle({
              ok: false,
              message: "Payment response was invalid.",
            });
            return;
          }
          settle({
            ok: true,
            data: {
              razorpay_payment_id: record.razorpay_payment_id,
              razorpay_order_id: record.razorpay_order_id,
              razorpay_signature: record.razorpay_signature,
            },
          });
        })
        .on("payment.error", (error) => {
          const { message, providerError } = readProviderError(error);
          // The one place the provider ever explains itself. When it refuses a
          // UPI submit no payment object is created at all, so this log and the
          // event that follows it are the *only* trace that anything happened —
          // Razorpay's own dashboard will show the order with nothing under it.
          logger.warn("[payments] razorpay_custom_payment_error", {
            method: data.method,
            orderId: data.order_id,
            code: providerError.code ?? "unknown",
            reason: providerError.reason ?? "unknown",
            source: providerError.source ?? "unknown",
            step: providerError.step ?? "unknown",
            providerPaymentId: providerError.paymentId ?? "none",
            description: message,
          });
          settle({ ok: false, message, providerError });
        });
    };

    try {
      if (typeof instance.createPayment !== "function") {
        logger.warn("[payments] razorpay_custom_sdk_unavailable", {
          customScriptLoaded: Boolean(
            document.querySelector(`script[src="${RAZORPAY_JS_SRC}"]`),
          ),
          checkoutScriptLoaded: Boolean(
            document.querySelector(
              `script[src="${RAZORPAY_CHECKOUT_SCRIPT_SRC}"]`,
            ),
          ),
          instanceKeys: Object.keys(instance as Record<string, unknown>),
        });
        settle({ ok: false, message: CUSTOM_SDK_UNAVAILABLE_MESSAGE });
        return;
      }
      const payment = instance.createPayment(paymentData, options);
      bind(payment ?? instance);
    } catch (error) {
      const { message, providerError } = readProviderError(error);
      logger.warn("[payments] razorpay_custom_payment_threw", {
        method: data.method,
        orderId: data.order_id,
        code: providerError.code ?? "unknown",
        reason: providerError.reason ?? "unknown",
        description: message,
      });
      settle({ ok: false, message, providerError });
    }
  });
}
